<template>
  <section class="checkout-issuing" :class="{ 'has-error': error, 'is-emitting': invoiceReady }" aria-labelledby="checkout-issuing-title">
    <div class="checkout-issuing__visual"><TicketIssuingAnimation :paused="Boolean(error)" /></div>
    <div class="checkout-issuing__copy">
      <span class="checkout-issuing__eyebrow">Paso 4 de 4 · {{ courtesy ? 'Cortesía' : 'Tus entradas' }}</span>
      <h2 ref="loadingTitle" id="checkout-issuing-title" tabindex="-1" class="checkout-issuing__title">{{ error ? 'No pudimos terminar la emisión.' : (title || 'Estamos preparando tus entradas.') }}</h2>
      <p class="checkout-issuing__description">{{ error || description || (free ? 'No necesitas realizar un pago. Te llevaremos a la confirmación cuando tus entradas estén listas.' : 'Estamos terminando tu compra. Te llevaremos a la confirmación cuando tus entradas estén listas.') }}</p>
      <div class="checkout-issuing__details">
        <strong v-if="eventName">{{ eventName }}</strong>
        <div><span v-if="count">{{ count }} {{ count === 1 ? 'entrada' : 'entradas' }}</span><span v-if="free">Sin pago</span></div>
      </div>
      <div v-if="!error" class="checkout-issuing__status" role="status">
        <span class="checkout-issuing__pulse" aria-hidden="true"></span>
        <div><strong>{{ invoiceReady ? 'Generando tus entradas' : 'Preparando tu orden' }}</strong><p>{{ slow ? 'Está tardando más de lo habitual. Seguimos trabajando; mantén esta página abierta.' : 'Mantén esta página abierta mientras terminamos.' }}</p></div>
      </div>
      <div v-else class="checkout-issuing__recovery" role="alert"><p>Revisa Mis tickets antes de volver a enviar la solicitud.</p><router-link :to="{ name: 'usertickets' }">Revisar Mis tickets <span aria-hidden="true">↗</span></router-link></div>
    </div>
  </section>
</template>

<script>
import TicketIssuingAnimation from './TicketIssuingAnimation.vue'

export default {
  name: 'CheckoutIssuingState',
  components: { TicketIssuingAnimation },
  props: {
    courtesy: Boolean, free: Boolean, invoiceReady: Boolean,
    count: { type: Number, default: 0 },
    eventName: { type: String, default: '' },
    title: { type: String, default: '' },
    description: { type: String, default: '' },
    error: { type: String, default: '' },
  },
  data() { return { slow: false, slowTimer: null, focusFrame: null } },
  mounted() {
    this.slowTimer = window.setTimeout(() => { this.slow = true }, 45000)
    // Wait for the payment form to collapse before positioning the loading card.
    this.focusFrame = window.requestAnimationFrame(() => {
      const card = this.$el.closest('.checkout-step--payment') || this.$el
      this.$refs.loadingTitle.focus({ preventScroll: true })
      card.scrollIntoView({
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth',
        block: 'start',
      })
    })
  },
  beforeUnmount() {
    window.clearTimeout(this.slowTimer)
    window.cancelAnimationFrame(this.focusFrame)
  },
}
</script>

<style scoped>
.checkout-issuing {
  width: 100%;
  display: grid;
  grid-template-columns: 290px minmax(0, 1fr);
  align-items: center;
  gap: 36px;
  color: #eef3fc;
  font-family: var(--fomo-font-body);
  text-align: left;
}
.checkout-issuing :is(p, span, strong) { font-family: inherit; }
.checkout-issuing__visual { width: 100%; max-width: 320px; align-self: center; position: relative; }
.checkout-issuing .checkout-issuing__eyebrow { display: block; margin-bottom: 12px; color: #aab8ce; font: 500 10px/1.4 var(--fomo-font-label); letter-spacing: .12em; text-transform: uppercase; }
.checkout-issuing .checkout-issuing__title { margin: 0; color: #eef3fc; font: 700 clamp(26px, 3vw, 34px)/1.16 var(--fomo-font-button); letter-spacing: -.04em; text-align: left; text-wrap: balance; }
.checkout-issuing .checkout-issuing__description { margin: 14px 0 0; max-width: 560px; color: #b8c3d5; font: 400 14px/1.65 var(--fomo-font-body); }
.checkout-issuing__title:focus { outline: none; }
.checkout-issuing__details { margin-top: 23px; display: grid; gap: 9px; }
.checkout-issuing__details > strong { color: #d7e0ee; font-size: 13px; line-height: 1.5; overflow-wrap: anywhere; }
.checkout-issuing__details > div { display: flex; flex-wrap: wrap; gap: 7px; }
.checkout-issuing__details span { padding: 5px 9px; border: 1px solid rgba(205, 216, 235, .12); border-radius: 7px; color: #aebdd1; font-size: 11px; line-height: 1.3; }
.checkout-issuing__status { display: flex; align-items: flex-start; gap: 12px; margin-top: 25px; padding-top: 21px; border-top: 1px solid rgba(205, 216, 235, .1); }
.checkout-issuing__pulse { flex: 0 0 6px; width: 6px; height: 6px; margin-top: 7px; border-radius: 50%; background: #d2dce9; animation: checkout-issuing-pulse 1.8s ease-in-out infinite; }
.checkout-issuing__status strong { font-size: 12px; font-weight: 600; color: #d7e0ee; line-height: 1.5; }
.checkout-issuing__status p, .checkout-issuing__recovery p { margin: 5px 0 0; font: 400 12px/1.6 var(--fomo-font-body); color: #99aac1; }
.checkout-issuing__recovery { margin-top: 22px; padding-top: 17px; border-top: 1px solid rgba(205, 216, 235, .1); }
.checkout-issuing__recovery a { display: inline-flex; align-items: center; min-height: 44px; gap: 10px; color: #edf3fc; font: 600 13px var(--fomo-font-button); text-decoration: underline; text-underline-offset: 4px; }
@keyframes checkout-issuing-pulse { 50% { opacity: .35; } }
@media (max-width: 720px) {
  .checkout-issuing { grid-template-columns: 1fr; gap: 22px; }
  .checkout-issuing__visual { width: min(100%, 290px); margin: -15px auto 0; order: 2; }
  .checkout-issuing__copy { order: 1; }
  .checkout-issuing .checkout-issuing__title { font-size: 27px; text-wrap: pretty; }
  .checkout-issuing .checkout-issuing__description { font-size: 13px; }
}
@media (prefers-reduced-motion: reduce) {
  .checkout-issuing__pulse { animation: none; }
}
</style>
